import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import { useForm, Controller } from 'react-hook-form';
import { BUTTON_BORDER_RADIUS, TEXTFIELD_BORDER_RADIUS } from 'constants/borderRadius';

/**
 * @typedef {object} PasswordStepProps
 * @property {string} email - User's email (displayed as read-only)
 * @property {Function} onSubmit - Callback when password is submitted
 * @property {Function} onBack - Callback to go back to email step
 * @property {boolean} [isLoading=false] - Shows loading state on button
 * @property {string} [errorMessage] - External error to display
 * @property {Function} [onClearError] - Callback to clear external error when user types
 */

/**
 * Password input step for email/password authentication.
 * Shows the email as a readOnly field and allows password entry.
 *
 * @param {PasswordStepProps} props - Component props
 * @returns {React.ReactElement}
 */
const PasswordStep = ({ email, onSubmit, onBack, isLoading = false, errorMessage, onClearError }) => {
    const [showPassword, setShowPassword] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'onSubmit',
        defaultValues: {
            password: '',
        },
    });

    /**
     * Toggles password visibility
     */
    const handleTogglePassword = () => {
        setShowPassword(prev => !prev);
    };

    /**
     * Handles form submission
     * @param {{password: string}} data - Form data
     */
    const onFormSubmit = (data) => {
        onSubmit(data.password);
    };

    return (
        <Box
            component="form"
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            sx={{ mt: 2 }}
        >
            {/* Email display - readOnly TextField for consistent UI */}
            <TextField
                id="email-display"
                variant="outlined"
                value={email}
                fullWidth
                slotProps={{
                    input: {
                        readOnly: true,
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon fontSize='small' color="disabled" />
                            </InputAdornment>
                        ),
                    },
                }}
                sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                        borderRadius: TEXTFIELD_BORDER_RADIUS,
                    },
                }}
            />

            <Controller
                name="password"
                control={control}
                rules={{
                    required: 'Password is required',
                }}
                render={({ field }) => (
                    <TextField
                        {...field}
                        onChange={(e) => {
                            field.onChange(e);
                            // Clear external error when user starts typing
                            if (errorMessage && onClearError) {
                                onClearError();
                            }
                        }}
                        id="password-input"
                        placeholder="Enter your password"
                        variant="outlined"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        autoComplete="current-password"
                        autoFocus
                        disabled={isLoading}
                        error={!!errors.password || !!errorMessage}
                        helperText={errors.password?.message || errorMessage || ' '}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon fontSize='small' color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            onClick={handleTogglePassword}
                                            edge="end"
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: TEXTFIELD_BORDER_RADIUS,
                            },
                        }}
                    />
                )}
            />

            <Button
                id="password-submit-btn"
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{ borderRadius: BUTTON_BORDER_RADIUS }}
            >
                {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>

            {/* Back link */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={onBack}
                    disabled={isLoading}
                    sx={{ cursor: 'pointer' }}
                >
                    Use a different email
                </Link>
            </Box>
        </Box>
    );
};

PasswordStep.propTypes = {
    email: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
    isLoading: PropTypes.bool,
    errorMessage: PropTypes.string,
    onClearError: PropTypes.func,
};

export default PasswordStep;
